import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Profile() {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: '', 
    email: '',
    phone: '',
    gender: '',
    dob: '',
    hometown: '',
    alternativeNumber: '',  
    profilePic: '',
    isVerified: false,
    createdAt: ''
  });

  const [editData, setEditData] = useState({ ...profileData });
  const [previewImage, setPreviewImage] = useState(null);

  const API_BASE_URL = 'https://api.boomontrip.com';

  const normalizeUserData = (userData) => {
    if (!userData) return null;
    
    return {
      _id: userData._id || userData.id || '',
      name: userData.name || userData.fullName || userData.userName || '',
      email: userData.email || userData.emailAddress || userData.mail || '',
      phone: userData.phone || userData.phoneNumber || userData.mobile || userData.mobileNumber || userData.contactNumber || '',
      gender: userData.gender || userData.sex || '',
      dob: userData.dob || userData.dateOfBirth || userData.birthDate || '',
      hometown: userData.hometown || userData.city || userData.homeCity || '',
      alternativeNumber: userData.alternativeNumber || userData.alternateNumber || userData.altPhone || userData.alternatePhone || userData.secondaryPhone || '',
      profilePic: userData.profilePic || userData.profilePicture || userData.avatar || userData.photo || '',
      isVerified: userData.isVerified || userData.verified || false,
      createdAt: userData.createdAt || userData.created_at || userData.registeredAt || ''
    };
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Fetching profile with token:', token ? 'Token exists' : 'No token'); 
      
      if (!token) {
        setError('No authentication token found. Please login again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);   

      if (response.ok) {
        const data = await response.json();
        console.log('Profile data received:', data); 
        
        // Handle different response formats
        let rawUserData = null;
        if (data.user) {
          rawUserData = data.user;
        } else if (data.data && data.data.user) {
          rawUserData = data.data.user;
        } else if (data.data) {
          rawUserData = data.data;
        } else {
          rawUserData = data;
        }

        // Normalize field names
        const userData = normalizeUserData(rawUserData);
        console.log('Normalized user data:', userData);

        setProfileData(userData);
        setEditData(userData);
        setError(null);
        
        localStorage.setItem('userData', JSON.stringify(userData));
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        setError(errorData.message || 'Failed to fetch profile');
        
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        setError('File size too large. Please choose an image under 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        compressImage(reader.result, (compressedImage) => {
          setPreviewImage(compressedImage);
          setEditData(prev => ({
            ...prev,
            profilePic: compressedImage
          }));
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const compressImage = (base64Str, callback) => {
    const img = new Image();
    img.src = base64Str;
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 800;
      const MAX_HEIGHT = 800;
      
      let width = img.width;
      let height = img.height;
      
      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
      callback(compressedBase64);
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      console.log('Saving profile with token:', token ? 'Token exists' : 'No token'); 
      
      if (!token) {
        setError('No authentication token found. Please login again.');
        setSaving(false);
        return;
      }

      console.log('Sending update data:', editData); 

      const response = await fetch(`${API_BASE_URL}/api/auth/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });

      console.log('Update response status:', response.status);

      if (response.ok) {
        const updatedData = await response.json();
        console.log('Updated profile data:', updatedData);
        
        // Handle different response formats
        let rawUserData = null;
        if (updatedData.user) {
          rawUserData = updatedData.user;
        } else if (updatedData.data && updatedData.data.user) {
          rawUserData = updatedData.data.user;
        } else if (updatedData.data) {
          rawUserData = updatedData.data;
        } else {
          rawUserData = updatedData;
        }

        // Normalize field names
        const userData = normalizeUserData(rawUserData);
        console.log('Normalized updated data:', userData);

        setProfileData(userData);
        setEditData(userData);
        setIsEditing(false);
        setPreviewImage(null);
        setError(null);
        
        localStorage.setItem('userData', JSON.stringify(userData));
        
        alert('Profile updated successfully!');
      } else {
        const errorData = await response.json();
        console.error('Update error:', errorData);
        setError(errorData.message || 'Failed to update profile');
        
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({ ...profileData });
    setPreviewImage(null);
    setIsEditing(false);
    setError(null);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('No authentication token found. Please login again.');
        setIsDeleting(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Clear all local storage
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        sessionStorage.clear();
        
        alert('Your account has been permanently deleted.');
        
        // Redirect to home page
        navigate('/');
      } else {
        const errorData = await response.json();
        console.error('Delete error:', errorData);
        setError(errorData.message || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-wrapper">
        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button 
              onClick={() => setError(null)} 
              className="error-close"
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        <div className="profile-header">
          <div className="header-background"></div>
          <div className="header-content">
            <h1 className="profile-title">Your Profile</h1>
            <p className="profile-subtitle">Manage your personal information</p>
          </div>
        </div>

        <div className="profile-card">
          <div className="profile-pic-section">
            <div className="pic-wrapper">
              <div className="profile-pic-container">
                {(previewImage || profileData.profilePic) ? (
                  <img
                    src={previewImage || profileData.profilePic}
                    alt="Profile"
                    className="profile-pic"
                  />
                ) : (
                  <div className="profile-pic-placeholder">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                )}
                {isEditing && (
                  <label className="upload-overlay">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="file-input"
                    />
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </label>
                )}
              </div>
              {profileData.isVerified && (
                <div className="verified-badge">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                  Verified
                </div>
              )}
            </div>
          </div>

          <div className="profile-info">
            {!isEditing ? (
              <div className="view-mode">
                <div className="info-grid">
                  <InfoField label="Full Name" value={profileData.name} icon={<PersonIcon />} />
                  <InfoField label="Email Address" value={profileData.email} icon={<EmailIcon />} />
                  <InfoField label="Phone Number" value={profileData.phone} icon={<PhoneIcon />} />
                  <InfoField label="Alternative Number" value={profileData.alternativeNumber} icon={<PhoneIcon />} />
                  <InfoField label="Gender" value={profileData.gender} icon={<GenderIcon />} />
                  <InfoField label="Date of Birth" value={formatDate(profileData.dob)} icon={<CalendarIcon />} />
                  <InfoField label="Hometown" value={profileData.hometown} icon={<LocationIcon />} />
                  <InfoField label="Member Since" value={formatDate(profileData.createdAt)} icon={<ClockIcon />} />
                </div>

                <div className="profile-actions">
                  <button className="edit-button" onClick={() => setIsEditing(true)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit Profile
                  </button>

                  <button className="delete-button" onClick={() => setShowDeleteModal(true)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Delete Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="edit-mode">
                <div className="form-grid">
                  <FormField label="Full Name" name="name" value={editData.name || ''} onChange={handleInputChange} placeholder="Enter your full name" />
                  <FormField label="Email Address" name="email" type="email" value={editData.email || ''} onChange={handleInputChange} placeholder="your.email@example.com" disabled />
                  <FormField label="Phone Number" name="phone" type="tel" value={editData.phone || ''} onChange={handleInputChange} placeholder="+91 XXXXX XXXXX" />
                  <FormField label="Alternative Number" name="alternativeNumber" type="tel" value={editData.alternativeNumber || ''} onChange={handleInputChange} placeholder="+91 XXXXX XXXXX" />
                  <FormSelect
                    label="Gender"
                    name="gender"
                    value={editData.gender || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'Select gender' },
                      { value: 'Male', label: 'Male' },
                      { value: 'Female', label: 'Female' },
                      { value: 'Other', label: 'Other' },
                    ]}
                  />
                  <FormField label="Date of Birth" name="dob" type="date" value={editData.dob ? editData.dob.split('T')[0] : ''} onChange={handleInputChange} />
                  <FormField label="Hometown" name="hometown" value={editData.hometown || ''} onChange={handleInputChange} placeholder="Enter your hometown" />
                </div>

                <div className="action-buttons">
                  <button className="cancel-button" onClick={handleCancel} disabled={saving}>
                    Cancel
                  </button>
                  <button className="save-button" onClick={handleSave} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="button-spinner"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <h2>Delete Account</h2>
                <p className="modal-warning">This action cannot be undone!</p>
              </div>

              <div className="modal-body">
                <div className="warning-list">
                  <div className="warning-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>All your personal information will be permanently deleted</span>
                  </div>
                  <div className="warning-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>All your bookings history will be lost</span>
                  </div>
                  <div className="warning-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>You will be logged out immediately</span>
                  </div>
                </div>

                <div className="confirmation-input">
                  <label>Type <strong>DELETE</strong> to confirm:</label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="Type DELETE"
                    className="delete-confirmation-input"
                    autoFocus
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="modal-cancel-button" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button 
                  className="modal-delete-button" 
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmation.toLowerCase() !== 'delete'}
                >
                  {isDeleting ? (
                    <>
                      <div className="button-spinner"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .profile-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .delete-button {
          flex: 1;
          padding: 14px 24px;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
        }

        .delete-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .modal-content {
          background: white;
          border-radius: 20px;
          max-width: 500px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s ease;
        }

        .modal-header {
          padding: 32px;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header svg {
          margin: 0 auto 16px;
        }

        .modal-header h2 {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 8px;
        }

        .modal-warning {
          color: #ef4444;
          font-size: 14px;
          font-weight: 600;
          margin: 0;
        }

        .modal-body {
          padding: 24px 32px;
        }

        .warning-list {
          background: #fef2f2;
          border: 1px solid #fee2e2;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .warning-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
          color: #991b1b;
          font-size: 14px;
        }

        .warning-item:last-child {
          margin-bottom: 0;
        }

        .warning-item svg {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .confirmation-input {
          margin-top: 20px;
        }

        .confirmation-input label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .confirmation-input strong {
          color: #ef4444;
        }

        .delete-confirmation-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 16px;
          transition: all 0.3s ease;
        }

        .delete-confirmation-input:focus {
          outline: none;
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          gap: 12px;
        }

        .modal-cancel-button {
          flex: 1;
          padding: 12px 24px;
          background: #f3f4f6;
          color: #374151;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-cancel-button:hover {
          background: #e5e7eb;
        }

        .modal-delete-button {
          flex: 1;
          padding: 12px 24px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .modal-delete-button:hover:not(:disabled) {
          background: #dc2626;
        }

        .modal-delete-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .profile-actions {
            flex-direction: column;
          }

          .modal-content {
            margin: 20px;
          }

          .modal-header {
            padding: 24px;
          }

          .modal-body {
            padding: 20px 24px;
          }

          .modal-footer {
            padding: 20px 24px;
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

// Helper Components
const InfoField = ({ label, value, icon }) => (
  <div className="info-field">
    <div className="info-label">
      <span className="info-icon">{icon}</span>
      {label}
    </div>
    <div className="info-value">{value || 'Not provided'}</div>
  </div>
);

const FormField = ({ label, name, type = 'text', value, onChange, placeholder, disabled = false }) => (
  <div className="form-field">
    <label className="form-label">{label}</label>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} className="form-input" disabled={disabled} />
  </div>
);

const FormSelect = ({ label, name, value, onChange, options }) => (
  <div className="form-field">
    <label className="form-label">{label}</label>
    <select name={name} value={value} onChange={onChange} className="form-select">
      {options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

const PersonIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
const EmailIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>);
const PhoneIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>);
const GenderIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>);
const CalendarIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
const LocationIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>);
const ClockIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);

export default Profile;